import React, { useState } from 'react';

import {
  Box,
  Button,
  Card,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import GroupIcon from '@material-ui/icons/Group';

import CardTitle from '../../../common/card/CardTitle';
import TrainingAddGroupDialog from '../dialogs/TrainingAddGroupDialog';
import TrainingDeleteGroupDialog from '../dialogs/TrainingDeleteGroupDialog';
import { useGetTrainingGroups } from '../Training.hooks';
import { Training } from '../Training.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { WorkflowEntity } from '../../workflow/Workflow.interface';

export interface TrainingAssignedGroupCardProps {
  training: Training;
}

const TrainingAssignedGroupCard: React.FC<TrainingAssignedGroupCardProps> = ({
  training
}) => {
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const { trainingGroups, get: getGroups } = useGetTrainingGroups(
    training.id,
    true
  );
  const [selectedGroups, setSelectedGroup] = useState([]) as any;
  const [openDeleteGroupDialog, setDeleteGroupDialog] = useState(false);

  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.Training>(
    training.task
  );
  const isDisabled: boolean = isFieldDisabled('groups');

  function isGroupSelected(trainingGroupId: number | undefined) {
    return selectedGroups.includes(trainingGroupId);
  }

  function handleSelectGroup(event: any, trainingGroupId: number | undefined) {
    if (event.target.checked) {
      setSelectedGroup([...selectedGroups, trainingGroupId]);
    } else {
      setSelectedGroup(
        selectedGroups.filter((n: number) => n !== trainingGroupId)
      );
    }
  }

  interface EnhancedTableToolbarProps {
    numSelected: number;
  }

  const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
    const { numSelected } = props;

    return (
      <Toolbar>
        {numSelected > 0 ? (
          <Typography
            style={{ flex: '1 1 100%' }}
            color='textSecondary'
            variant='subtitle1'
          >
            {numSelected} selected
          </Typography>
        ) : (
          <CardTitle style={{ marginLeft: 0, paddingLeft: 0 }}>
            <GroupIcon />
            Assign Teams
          </CardTitle>
        )}
        {numSelected > 0 ? (
          <Tooltip title='Delete' onClick={() => setDeleteGroupDialog(true)}>
            <IconButton aria-label='delete'>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <div></div>
        )}
      </Toolbar>
    );
  };

  return (
    <Card>
      <EnhancedTableToolbar numSelected={selectedGroups.length} />

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding='checkbox'></TableCell>
              <TableCell>Team</TableCell>
              <TableCell>Completed</TableCell>
              <TableCell>In Progress</TableCell>
              <TableCell>Not Started</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {training &&
              trainingGroups &&
              trainingGroups.map(trainingGroup => (
                <TableRow key={trainingGroup.groupId}>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      color='primary'
                      checked={isGroupSelected(trainingGroup.groupId)}
                      onChange={event =>
                        handleSelectGroup(event, trainingGroup.groupId)
                      }
                      disabled={isDisabled}
                    />
                  </TableCell>
                  <TableCell>{trainingGroup.groupName}</TableCell>
                  <TableCell>{trainingGroup.completedCount}</TableCell>
                  <TableCell>{trainingGroup.inProgressCount}</TableCell>
                  <TableCell>{trainingGroup.toDoCount}</TableCell>
                </TableRow>
              ))}
            <TableRow>
              <TableCell colSpan={4}>
                <Box display='flex' alignItems='center'>
                  <Button
                    variant='outlined'
                    color='primary'
                    startIcon={<AddIcon />}
                    disabled={
                      isDisabled && training.version === 1
                    } /* after first approval the training version is incremented */
                    onClick={() => setIsOpenDialog(true)}
                  >
                    Add a Team
                  </Button>
                  {training.version === 1 && (
                    <Box ml={2}>
                      Please approve a training before assigning a team.
                    </Box>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {isOpenDialog && (
        <TrainingAddGroupDialog
          open={isOpenDialog}
          training={training}
          existingGroups={trainingGroups || []}
          onClose={() => setIsOpenDialog(false)}
          onAdded={() => {
            getGroups();
            setIsOpenDialog(false);
          }}
        />
      )}

      {openDeleteGroupDialog && selectedGroups && training && (
        <TrainingDeleteGroupDialog
          open={openDeleteGroupDialog}
          selectedGroups={selectedGroups}
          onClose={() => setDeleteGroupDialog(false)}
          onDelete={() => {
            getGroups();
            setSelectedGroup([]);
            setDeleteGroupDialog(false);
          }}
          training={training}
        />
      )}
    </Card>
  );
};

export default TrainingAssignedGroupCard;
